// client/src/pages/ChoosePlan.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings.js';
import { useAuth } from '../hooks/useAuth.js';
import ProgressSteps from '../components/ui/ProgressSteps.js';

const steps = [
  { id: 'account', label: 'Create Account' },
  { id: 'choose-plan', label: 'Choose Plan' },
  { id: 'payment', label: 'Payment' },
];

export default function ChoosePlan() {
  const { userTypes } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Split tiers: Free (one card) + paid tiers (rest of the row).
  // Free becomes a real plan card so users see it as a valid choice
  // rather than a "skip" — that wording was confusing them.
  const allTiers = userTypes || [];
  const freeTier = allTiers.find((t) => !t.ispaid && t.isenabled);
  const paidTiers = allTiers.filter((t) => t.ispaid && t.isenabled);

  // Total cards drives the grid: with Free we usually have 4 (Free +
  // Stardust + Fairydust + Business Wizard). The grid adapts so 2 or 3
  // cards still center cleanly without huge gaps.
  const totalCards = (freeTier ? 1 : 0) + paidTiers.length;
  const gridCols = totalCards <= 2
    ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl'
    : totalCards === 3
    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl'
    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl';

  const handleSelectPaid = (tier) => {
    navigate(`/checkout?tier=${tier.usertypeid}&interval=${billingCycle}`);
  };

  const handleSelectFree = () => {
    // User stays on the Free tier they were assigned at registration.
    // Nothing to update server-side — just send them into the app.
    navigate('/');
  };

  const handleRedeemGift = () => {
    // /gift/accept is public — works whether the user is logged in or
    // not. Since this page is behind PrivateRoute, every visitor here
    // IS logged in, but we keep a single entry point for consistency
    // with the Pricing page and the gift email link.
    navigate('/gift/accept');
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <ProgressSteps steps={steps} currentStep="choose-plan" />

        {/* Heading */}
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl text-gold-dark dark:text-gold mb-2">Choose Your Plan</h1>
          <p className="text-royal-700 dark:text-white">
            Welcome{user?.firstname ? `, ${user.firstname}` : ''}! Pick the plan that fits you best.
          </p>
        </div>

        {/* Billing toggle — only shown if there are paid tiers worth toggling */}
        {paidTiers.length > 0 && (
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-royal-900 dark:bg-royal-800 border border-royal-700 dark:border-royal-600 rounded-full p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 rounded-full text-base font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-gold text-royal-900'
                    : 'text-white hover:text-gold'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 py-2 rounded-full text-base font-medium transition-all ${
                  billingCycle === 'yearly'
                    ? 'bg-gold text-royal-900'
                    : 'text-white hover:text-gold'
                }`}
              >
                Yearly
              </button>
            </div>
          </div>
        )}

        {/* Plan cards — Free + paid tiers in one row */}
        <div className={`grid gap-5 mx-auto mb-10 ${gridCols}`}>
          {/* Free card — same shape as paid cards but with muted styling
              so it reads as a valid choice without competing visually
              with paid tiers. */}
          {freeTier && (
            <div className="rounded-xl border border-royal-700 dark:border-royal-600 bg-royal-900 dark:bg-royal-800/40 p-6 flex flex-col">
              <h3 className="font-display text-xl text-white mb-1">{freeTier.typename}</h3>
              <p className="text-royal-100 text-sm mb-4">{freeTier.tagline}</p>

              <div className="mb-4">
                <span className="text-3xl font-bold text-white">$0</span>
                <span className="text-royal-100 text-sm">/forever</span>
              </div>

              <ul className="space-y-2 mb-6 flex-1">
                {(freeTier.features || []).map((f, i) => (
                  <li key={i} className="text-sm text-white flex items-start gap-2">
                    <span className="text-royal-100 mt-0.5 shrink-0">{'\u2713'}</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={handleSelectFree}
                className="w-full py-2.5 rounded-lg font-medium text-base bg-royal-700 text-white hover:bg-royal-600 transition-all"
              >
                Continue with Free
              </button>
            </div>
          )}

          {/* Paid tiers */}
          {paidTiers.map((tier) => {
            const monthly = parseFloat(tier.pricemonthly) || 0;
            const yearly = parseFloat(tier.priceyearly) || 0;
            const price = billingCycle === 'yearly' ? yearly : monthly;
            const savePct = monthly > 0 ? Math.round((1 - yearly / (monthly * 12)) * 100) : 0;

            return (
              <div
                key={tier.usertypeid}
                className={`relative rounded-xl border p-6 flex flex-col transition-all ${
                  tier.ispopular
                    ? 'border-gold bg-royal-900 dark:bg-royal-800/80 shadow-lg shadow-gold/10'
                    : 'border-royal-700 dark:border-royal-600 bg-royal-900 dark:bg-royal-800/40'
                }`}
              >
                {tier.ispopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-royal-900 text-sm font-bold px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}

                <h3 className="font-display text-xl text-gold mb-1">{tier.typename}</h3>
                <p className="text-royal-100 text-sm mb-4">{tier.tagline}</p>

                <div className="mb-4">
                  <span className="text-3xl font-bold text-white">${price.toFixed(2)}</span>
                  <span className="text-royal-100 text-sm">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  {billingCycle === 'yearly' && savePct > 0 && (
                    <span className="ml-2 text-green-400 text-sm font-medium">Save {savePct}%</span>
                  )}
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {(tier.features || []).map((f, i) => (
                    <li key={i} className="text-sm text-white flex items-start gap-2">
                      <span className="text-gold mt-0.5 shrink-0">{'\u2713'}</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPaid(tier)}
                  className={`w-full py-2.5 rounded-lg font-medium text-base transition-all ${
                    tier.ispopular
                      ? 'bg-gold text-royal-900 hover:bg-gold-light'
                      : 'bg-royal-700 text-white hover:bg-royal-600'
                  }`}
                >
                  Select {tier.typename}
                </button>
              </div>
            );
          })}
        </div>

        {/* ─── Divider ─────────────────────────────────── */}
        <div className="flex items-center gap-3 max-w-md mx-auto mb-6">
          <div className="flex-1 h-px bg-royal-300 dark:bg-royal-700/60" />
          <span className="text-royal-500 dark:text-royal-200 text-sm whitespace-nowrap">or, if you have one</span>
          <div className="flex-1 h-px bg-royal-300 dark:bg-royal-700/60" />
        </div>

        {/* ─── Gift code redemption section ────────────────
            Placed at the bottom because for most users this is a
            secondary path — they'll pick a plan first. The gold-tinted
            styling makes sure users who DID come here to redeem can
            still spot it easily.

            Visible to everyone (no `if (user)` check needed): the
            /gift/accept page is public and handles auth state itself,
            and ChoosePlan is already behind PrivateRoute so everyone
            here is logged in anyway. Keeping this unconditional means
            one consistent entry point regardless of how the user
            arrived. */}
        <div className="max-w-md mx-auto">
          <div className="rounded-xl border border-gold/40 bg-gold/5 dark:bg-gold/10 p-5 sm:p-6 text-center">
            <div className="text-3xl mb-2">🎁</div>
            <h3 className="font-display text-lg text-gold-dark dark:text-gold mb-1">
              Have a Gift Code?
            </h3>
            <p className="text-royal-700 dark:text-royal-100 text-sm mb-4">
              Redeem it to unlock your subscription.
            </p>
            <button
              onClick={handleRedeemGift}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
            >
              Redeem a Gift
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}